import React, {useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {Button, Col, Container, Row} from 'react-bootstrap';
import {
	ConfirmationDialog
} from "../../Common/ConfirmationDialog";
import {
	GenericModelViewer
} from "../../Common/GenericModelViewer";
import {
    Glyphicon
} from "../../Common/GlyphIcon";
import {
    DocumentUtils,
    GuidUtils,
    ObjectUtils,
} from "../../../Utils";
import {service as ContactService} from '../../../Services/ContactService';
import {service as ProjectService} from '../../../Services/ProjectService';
import {
	Stakeholder
} from "../../../Models";
import {
    useCheckAuth,
} from "../../../Hooks/RouteHooks";
import {
    useMakePageRequest
} from "../../../Hooks/RequestHooks";
import {
    useEditContextNodeId,
    usePermission
} from "../../../Hooks/PermissionHooks";

import {useContextIndependentPage} from "../../../Hooks/ContextBuilderHooks";
import {useClassNames} from "../../../Hooks/StyleHooks";

let model = new Stakeholder();


export const ContactsViewPage = props => {
    useCheckAuth();
    useContextIndependentPage(props);

    const classNames = useClassNames('sb-contacts-view-page');
    const makePageRequest = useMakePageRequest();
    const permission = usePermission();
    const editContextNodeId = useEditContextNodeId();

    const currentUser = useSelector(state => state.userState.user);
    const currentContext = useSelector(state => state.contextState.context);

    const [contact, setContact] = useState(null);
    const [existingContact, setExistingContact] = useState(null);
    const [contactValidation, setContactValidation] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);

    const contactId = props?.match?.params?.conId;

    useEffect(() => {
        loadData();
    }, [contactId, currentContext?.project, currentUser?.organizationId]);

    const loadData = () => {
        if(contactId === "new") {

            //if we have a user pulled. let's default to their orgId.
            let userOrganizationId = currentUser?.organizationId;

            if(currentContext?.project) {
                //if a project is in context, lets get it and use that to populate that the relationship.
                makePageRequest(
                    () => ProjectService.GetProject(currentContext.project),
                    (x) => {
                        //if we have a project. We know the orgId to use.
                        const { projectId, contextNodeId, organizationId } = x.result;
                        let defaultProjectStakeholderRelationships = [];

                        // if the current context is editable, then prepopulate a default
                        // ProjectStakeholderRelationship for it
                        if(editContextNodeId(contextNodeId)) {
                            defaultProjectStakeholderRelationships.push(
                                {
                                    _tempId: GuidUtils.Generate(),
                                    projectId,
                                    contextNodeId
                                }
                            )
                        }

                        setContact({
                            stakeholderTypeId: 1,
                            ProjectStakeholderRelationship: defaultProjectStakeholderRelationships,
                            ...contact,
                            // use the user's orgId only if there is not a relationship
                            // already assigned for a different organizationId
                            organizationId: organizationId ?? userOrganizationId,
                        });
                        setEditMode(true);
                    }
                );
            } else {
                //else create an empty contact
                setContact({
                    organizationId: userOrganizationId,
                    stakeholderTypeId: 1,
                });
                setEditMode(true);
            }
        } else {
            // don't reload if we haven't changed
            if(contactId === contact?.contactId) {
                return;
            }
            
            //fetch contact and load that data
            makePageRequest(
                () => ContactService.GetContact(contactId),
                (x) => {
                    setContact(x.result);
                    setEditMode(false);
                }
            );
        }
    };

    const enterEditMode = () => {
        //set edit mode state
        setEditMode(true);
        setExistingContact(ObjectUtils.Clone(contact));
    };

    const exitEditMode = () => {
        //exit edit mode. Either replace with the old version, or change the page back.
        if(existingContact) {
            setEditMode(false);
            setContact(ObjectUtils.Clone(existingContact));
            setExistingContact(null);
        } else {
            let newPath = window.location.pathname.replace("/contacts/new", "/contacts");
            DocumentUtils.changePage(newPath);
        }
    };

    const updateContact = (contact) => {
        setContact(contact);
    };

    const saveContact = () => {
        //save the contact against the api.
        if(contact.stakeholderId) {
            makePageRequest(
                () => ContactService.UpdateContact(contact),
                (x) => {
                    setEditMode(false);
                    setContact(x.result);
                }
            );
        } else {
            makePageRequest(
                () => ContactService.CreateContact(contact),
                (x) => {
                    setEditMode(false);
                    setContact(x.result);
                    let newPath = window.location.pathname.replace("new", x.result.stakeholderId);
                    DocumentUtils.changePage(newPath);
                }
            );
        }
    };

    const deleteContact = () => {
        //make delete ajax request
        makePageRequest(
            () => ContactService.DeleteContact(contact.stakeholderId),
            (x) => DocumentUtils.changePage("/")
        );
    };

    const updateValidation = (validation, editorGuid) => {
        //update the validator result
        setContactValidation(ObjectUtils.Clone(validation));
    };

    const isValid = () => {
        //check all validators we're getting from the model editor.
        for(let propertyName in contactValidation) {
            let validation = contactValidation[propertyName];

            if(!validation.valid) {
                return false;
            }
        }

        return true;
    };

    const canEdit = () => {
        if(contact) {
            if(permission('update_contact')) {
                if(contact.ProjectStakeholderRelationship?.length) {
                    return contact.ProjectStakeholderRelationship.some((x) =>
                        editContextNodeId(x.contextNodeId)
                    );
                } else {
                    //if no projects are assigned users who can edit outside of assignments can still edit. so lets give them access.
                    return editContextNodeId(null);
                }
            }
        }
        return false;
    };

    const canDelete = () => {
        if(contact) {
            if(permission('delete_contact')) {
                if(contact.ProjectStakeholderRelationship?.length) {
                    return contact.ProjectStakeholderRelationship.some((x) =>
                        editContextNodeId(x.contextNodeId)
                    );
                } else {
                    //if no projects are assigned users who can edit outside of assignments can still edit. so lets give them access.
                    return editContextNodeId(null);
                }
            }
        }

        return false;
    };

    const getContactName = () => {
        let contactName = ContactService.GetTitle(contact);
        return contactName !== "" ? contactName : "New Contact";
    };

    // don't render anything until we are loaded.
    if(contact === null) {
        return null;
    }

    return <Container className={classNames}>
        <Row>
            <ConfirmationDialog
                Title="Confirmation"
                Body="Are you sure you want to delete this contact?"
                Display={deleteDialog}
                OnClose={() => setDeleteDialog(false)}
                OnConfirm={() => deleteContact()}
            />
            <Col xs={12} md={12}>
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            editMode ? <React.Fragment>
                                    <Button
                                        variant="save"
                                        onClick={() => saveContact()}
                                        disabled={!isValid()}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="cancel"
                                        onClick={() => exitEditMode()}
                                    >
                                        Cancel
                                    </Button>
                                    {
                                        contactId !== 'new' && canDelete() ?
                                            <Button
                                                variant="delete"
                                                onClick={() => setDeleteDialog(true)}
                                            >
                                                Delete
                                            </Button> :
                                            null
                                    }
                                </React.Fragment> :
                                (
                                    canEdit() ?
                                        <Button
                                            variant="link"
                                            onClick={() => enterEditMode()}>
                                            <Glyphicon glyph="pencil" />Edit
                                        </Button>:
                                        null
                                )
                        }
                    </div>
                    {getContactName()}
                </h2>

                {
                    contact ?
                        <GenericModelViewer
                            IsNewModel={contactId === "new"}
                            EditMode={editMode}
                            Data={contact}
                            Update={updateContact}
                            OnValidationUpdate={updateValidation}
                            Model={model}
                        />:
                        null
                }
            </Col>
        </Row>
    </Container>;
};


